// Persist only presentation settings. Data, permissions and translated labels stay in headers.
export function columnWidth (header, width = header.width) {
  const minimum = Number(header.minWidth) || 80
  const numericWidth = typeof width === 'number' ? width : /^\d+(px)?$/.test(width || '') ? parseInt(width, 10) : 200
  return Math.round(Math.max(minimum, Math.min(1200, Number.isFinite(numericWidth) ? numericWidth : 200)))
}

export function mergeColumns (headers, saved = []) {
  const defaults = headers.map((header, index) => ({
    ...header,
    visible: true,
    width: columnWidth(header),
    pin: index === headers.length - 1 ? 'right' : null
  }))
  const available = new Map(defaults.map(column => [column.value, column]))
  const columns = []
  for (const setting of Array.isArray(saved) ? saved : []) {
    if (!setting || !available.has(setting.value)) continue
    const header = available.get(setting.value)
    columns.push({
      ...header,
      visible: header.constructorLocked || setting.visible !== false,
      width: columnWidth(header, setting.width),
      pin: header.constructorLocked ? header.pin : ['left', 'right'].includes(setting.pin) ? setting.pin : null
    })
    available.delete(setting.value)
  }
  columns.push(...available.values())
  if (columns.length && !columns.some(column => column.visible)) columns[0].visible = true
  return ['left', null, 'right'].flatMap(side => {
    const group = columns.filter(column => column.pin === side && !column.constructorLocked)
    const defaultGroup = defaults.filter(column => column.pin === side)
    // Anchor locked columns to their default edge, ignoring saved visibility, pin and order.
    if (side === 'right') {
      group.reverse()
      defaultGroup.reverse()
    }
    defaultGroup.forEach((column, index) => {
      if (column.constructorLocked) {
        group.splice(index, 0, columns.find(item => item.value === column.value))
      }
    })
    return side === 'right' ? group.reverse() : group
  })
}

export function saveableColumns (columns) {
  return columns.map(({ value, visible, width, pin }) => ({ value, visible, width, pin }))
}

export function visibleColumns (columns) {
  const visible = columns.filter(column => column.visible)
  return ['left', null, 'right'].flatMap(side => visible.filter(column => column.pin === side))
}

export function pinnedOffsets (columns, widths = {}) {
  const offsets = {}
  let left = 0
  let right = 0
  for (const column of columns) {
    if (column.pin !== 'left') continue
    offsets[column.value] = { left: `${left}px` }
    left += widths[column.value] || column.width
  }
  for (const column of [...columns].reverse()) {
    if (column.pin !== 'right') continue
    offsets[column.value] = { right: `${right}px` }
    right += widths[column.value] || column.width
  }
  return offsets
}

export function columnVariables (columns, widths = {}) {
  const offsets = pinnedOffsets(columns, widths)
  const variables = {
    '--configurable-table-width': `${columns.reduce((sum, column) => sum + column.width, 0)}px`
  }
  columns.forEach((column, index) => {
    variables[`--table-column-${index}-width`] = `${column.width}px`
    if (column.pin) variables[`--table-column-${index}-offset`] = offsets[column.value][column.pin]
  })
  return variables
}

export function columnLayout (columns, widths = {}) {
  const styles = {}
  columns.forEach((column, index) => {
    const width = `var(--table-column-${index}-width)`
    styles[column.value] = { width, minWidth: width, maxWidth: width }
    if (column.pin) styles[column.value][column.pin] = `var(--table-column-${index}-offset)`
  })
  const variables = columnVariables(columns, widths)
  return { variables, styles }
}
